import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Source, Layer, Marker } from "react-map-gl";
import MapGL from "react-map-gl";
import type { LayerProps, ViewStateChangeEvent } from "react-map-gl";
import { TripperData, StopBlock } from "../types";
import {
  useTripperScrollContext,
  useTripperWidgetContext,
} from "../context/TripperWidgetContext";
import {
  buildBoundsFromCoordinates,
  fitMapToBounds,
  flattenStopBlocks,
  getStopCoordinates,
} from "../utils/mapHelpers";
import {
  findGroupBlock,
  getActiveGroupStopIds,
  collectAllOptionsGroupStopIds,
} from "../utils/groupHelpers";
import { buildActiveMarkerIds } from "../utils/markerActiveState";
import {
  isStopVisibleForBusinessTypeFilter,
  selectionToKey,
} from "../utils/businessTypeFilters";
import { extractOutlineCoordinates } from "../utils/cameraIntent";
import { TripperAutoFollowToggle } from "./TripperAutoFollowToggle";
import { TripperMapResetButton } from "./TripperMapResetButton";
import { normalizeMapStyleVariant } from "../../../apps/tripper/components/mapStyleTypes";
import { normalizeHexColor } from "../accentColorHelpers";

function isTrailGeometry(
  outline?: { type?: string } | null,
): outline is GeoJSON.LineString | GeoJSON.MultiLineString {
  return outline?.type === "LineString" || outline?.type === "MultiLineString";
}

function applyMapboxBasemapTheme(
  map: { setConfigProperty?: (a: string, b: string, c: unknown) => void },
  theme: string,
  show3dObjects = true,
  show3dBuildings = true,
  show3dTrees = true,
  showPoi = true,
) {
  try {
    map.setConfigProperty?.("basemap", "theme", theme);
    map.setConfigProperty?.("basemap", "show3dObjects", show3dObjects);
    map.setConfigProperty?.("basemap", "show3dBuildings", show3dBuildings);
    map.setConfigProperty?.("basemap", "show3dTrees", show3dTrees);
    map.setConfigProperty?.("basemap", "showPointOfInterestLabels", showPoi);
  } catch {
    /* Style may not be Mapbox Standard */
  }
}

type TripperMapProps = {
  data: TripperData;
  onMarkerClick: (itemId: string) => void;
  compactMobile?: boolean;
  hideAutoFollow?: boolean;
  hideControls?: boolean;
};

export const TripperMap = ({ data, onMarkerClick, compactMobile, hideAutoFollow, hideControls }: TripperMapProps) => {
  const { activeItemId, activeStopGroupId, isMetadataInView } = useTripperScrollContext();
  const { atlasMode, mapRef, isAutoFollowActive, setIsAutoFollowActive, notifyMapInstanceReady, businessTypeFilterSelection, businessTypeFiltersEnabled } =
    useTripperWidgetContext();

  const preserveMapPositionOnFilterChange = atlasMode && businessTypeFiltersEnabled;

  const trailOutline = data.itinerary?.trailPlace?.outline ?? null;
  const hasTrailOverlay = isTrailGeometry(trailOutline);
  const trailCoordinates = useMemo(
    () =>
      hasTrailOverlay
        ? extractOutlineCoordinates(trailOutline as GeoJSON.Geometry)
        : [],
    [hasTrailOverlay, trailOutline],
  );

  const allStopCoordinates = useMemo(
    () => flattenStopBlocks(data.segments)
      .map(stop => getStopCoordinates(stop, data.operatorByEntityId))
      .filter((coords): coords is [number, number] => Boolean(coords)),
    [data.segments, data.operatorByEntityId],
  );

  const defaultMapBounds = useMemo(
    () => buildBoundsFromCoordinates([...allStopCoordinates, ...trailCoordinates]),
    [allStopCoordinates, trailCoordinates],
  );

  const mapMarkers = useMemo(() => {
    let markerNumber = 0;
    const markers: Array<{
      stop: StopBlock;
      coordinates: [number, number];
      markerNumber: number;
    }> = [];

    for (const stop of flattenStopBlocks(data.segments)) {
      if (
        businessTypeFiltersEnabled
        && !isStopVisibleForBusinessTypeFilter(
          stop,
          data.operatorByEntityId,
          businessTypeFilterSelection,
        )
      ) {
        continue;
      }

      markerNumber += 1;
      const coordinates = getStopCoordinates(stop, data.operatorByEntityId);
      if (!coordinates) continue;

      markers.push({ stop, coordinates, markerNumber });
    }

    return markers;
  }, [data.segments, data.operatorByEntityId, businessTypeFilterSelection, businessTypeFiltersEnabled]);

  const stops = useMemo(
    () => mapMarkers.map(marker => ({ item: marker.stop, coordinates: marker.coordinates })),
    [mapMarkers],
  );

  const bounds = useMemo(
    () =>
      buildBoundsFromCoordinates([
        ...stops.map(stop => stop.coordinates),
        ...trailCoordinates,
      ]),
    [stops, trailCoordinates],
  );
  const mapBounds = preserveMapPositionOnFilterChange ? defaultMapBounds : bounds;
  const boundsCenter = useMemo(() => mapBounds?.getCenter(), [mapBounds]);

  const activeGroupStopIds = useMemo(
    () => getActiveGroupStopIds(data.segments, activeStopGroupId),
    [activeStopGroupId, data.segments],
  );

  const allOptionsGroupStopIds = useMemo(
    () => collectAllOptionsGroupStopIds(data.segments),
    [data.segments],
  );

  // Find the active group's place for outline rendering
  const activeGroup = useMemo(
    () => findGroupBlock(data.segments, activeStopGroupId) ?? null,
    [activeStopGroupId, data.segments],
  );

  const isOptionsGroup = activeGroup?.groupType === "OPTIONS";

  const activeMarkerIds = useMemo(
    () => buildActiveMarkerIds(activeItemId, activeGroupStopIds, allOptionsGroupStopIds, isOptionsGroup),
    [activeItemId, activeGroupStopIds, allOptionsGroupStopIds, isOptionsGroup],
  );

  const activePlace = activeGroup?.place ?? null;
  const placeOutline = activePlace?.outline as
    | GeoJSON.Feature<GeoJSON.Polygon>
    | GeoJSON.Polygon
    | null
    | undefined;

  // Mapbox GL doesn't support CSS custom properties — use hex values.
  // Normalize so a stored value missing its leading "#" (e.g. "5696CD")
  // doesn't trigger Mapbox's "color expected" error.
  const routeColor = normalizeHexColor(
    data.itinerary?.tripperSettings?.primaryColor,
    "#66800B",
  );
  const routeLayerStyle = useMemo<LayerProps>(() => ({
    id: "tripper-route",
    type: "line",
    paint: {
      "line-color": routeColor,
      "line-width": 3,
      "line-opacity": 0.75,
    },
  }), [routeColor]);

  const placeFillStyle = useMemo<LayerProps>(() => ({
    id: "tripper-place-fill",
    type: "fill",
    paint: {
      "fill-color": routeColor,
      "fill-opacity": 0.08,
    },
  }), [routeColor]);

  const placeLineStyle = useMemo<LayerProps>(() => ({
    id: "tripper-place-line",
    type: "line",
    paint: {
      "line-color": routeColor,
      "line-width": 3,
      "line-opacity": 0.5,
    },
  }), [routeColor]);

  const trailLayerStyle = useMemo<LayerProps>(() => ({
    id: "tripper-trail",
    type: "line",
    paint: {
      "line-color": routeColor,
      "line-width": 3,
      "line-opacity": 0.75,
    },
  }), [routeColor]);

  const mapTheme = normalizeMapStyleVariant(
    data.itinerary?.tripperSettings?.mapStyleVariant,
  );
  const map3dObjects = data.itinerary?.tripperSettings?.map3dObjects ?? true;
  const map3dBuildings = data.itinerary?.tripperSettings?.map3dBuildings ?? true;
  const map3dTrees = data.itinerary?.tripperSettings?.map3dTrees ?? true;
  const showPoi = data.itinerary?.tripperSettings?.showPointsOfInterest ?? false;
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapViewCustomized, setMapViewCustomized] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasRevealedRef = useRef(false);
  const containerSizeRef = useRef({ width: 0, height: 0 });
  const boundsRef = useRef(bounds);
  boundsRef.current = bounds;
  const defaultBoundsRef = useRef(defaultMapBounds);
  defaultBoundsRef.current = defaultMapBounds;
  const mapViewCustomizedRef = useRef(false);
  mapViewCustomizedRef.current = mapViewCustomized;

  const markMapViewCustomized = useCallback(() => {
    if (!atlasMode) return;
    mapViewCustomizedRef.current = true;
    setMapViewCustomized(true);
  }, [atlasMode]);

  const resetMapView = useCallback(() => {
    const targetBounds = preserveMapPositionOnFilterChange
      ? defaultBoundsRef.current
      : boundsRef.current;
    fitMapToBounds(mapRef.current, targetBounds, { animated: true, padding: 64 });
    mapViewCustomizedRef.current = false;
    setMapViewCustomized(false);
  }, [mapRef, preserveMapPositionOnFilterChange]);

  const boundsKey = useMemo(
    () => stops.map(stop => `${stop.coordinates[0]},${stop.coordinates[1]}`).join("|"),
    [stops],
  );

  const defaultBoundsKey = useMemo(
    () => allStopCoordinates.map(coords => `${coords[0]},${coords[1]}`).join("|"),
    [allStopCoordinates],
  );

  const fitMapToStops = useCallback((animated: boolean) => {
    return fitMapToBounds(mapRef.current, boundsRef.current, { animated });
  }, [mapRef]);

  const fitMapToDefaultView = useCallback((animated: boolean) => {
    return fitMapToBounds(mapRef.current, defaultBoundsRef.current, { animated, padding: 64 });
  }, [mapRef]);

  const ensureMapFitted = useCallback((tryFitFn: () => boolean, onComplete?: () => void) => {
    let attempts = 0;
    const maxAttempts = 60;
    let observer: ResizeObserver | null = null;
    let intervalId: number | null = null;

    const cleanup = () => {
      observer?.disconnect();
      if (intervalId !== null) window.clearInterval(intervalId);
    };

    const attemptFit = () => {
      if (tryFitFn()) {
        mapRef.current?.getMap()?.resize();
        cleanup();
        onComplete?.();
        return true;
      }
      return false;
    };

    if (attemptFit()) return;

    const container = containerRef.current;
    if (container) {
      observer = new ResizeObserver(() => {
        attemptFit();
      });
      observer.observe(container);
    }

    intervalId = window.setInterval(() => {
      attempts += 1;
      if (attemptFit() || attempts >= maxAttempts) {
        mapRef.current?.getMap()?.resize();
        cleanup();
        onComplete?.();
      }
    }, 50);
  }, [mapRef]);

  // Single bootstrap path: debounce bounds changes while hidden, fit once, then reveal.
  useEffect(() => {
    if (!mapLoaded || !defaultMapBounds) return;
    if (hasRevealedRef.current && preserveMapPositionOnFilterChange) return;

    const debounceMs = hasRevealedRef.current ? 0 : 120;
    const tryFit = preserveMapPositionOnFilterChange
      ? () => fitMapToDefaultView(false)
      : () => fitMapToStops(false);

    const timer = window.setTimeout(() => {
      ensureMapFitted(tryFit, () => {
        if (hasRevealedRef.current) return;
        hasRevealedRef.current = true;
        setMapReady(true);
        notifyMapInstanceReady();
      });
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [
    mapLoaded,
    preserveMapPositionOnFilterChange ? defaultBoundsKey : boundsKey,
    atlasMode,
    preserveMapPositionOnFilterChange,
    ...(preserveMapPositionOnFilterChange ? [] : [selectionToKey(businessTypeFilterSelection)]),
    ensureMapFitted,
    defaultMapBounds,
    bounds,
    fitMapToDefaultView,
    fitMapToStops,
    notifyMapInstanceReady,
  ]);

  // Refit when bounds change after the map is already visible.
  useEffect(() => {
    if (!mapReady || !bounds) return;
    if (preserveMapPositionOnFilterChange) return;
    if (atlasMode && mapViewCustomizedRef.current) return;
    fitMapToStops(false);
  }, [boundsKey, mapReady, preserveMapPositionOnFilterChange, atlasMode, fitMapToStops, bounds]);

  // Markers must mount after reveal — Mapbox DOM markers fail to paint when
  // added while the container is hidden (visibility/opacity 0 sizing).
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    const syncMarkers = () => {
      map.resize();
      map.triggerRepaint();
    };

    syncMarkers();
    const timer = window.setTimeout(syncMarkers, 0);
    return () => window.clearTimeout(timer);
  }, [mapReady, boundsKey, mapMarkers.length, mapRef]);

  useEffect(() => {
    if (!mapReady || !bounds || !containerRef.current) return;

    let rafId: number | null = null;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;
      const prev = containerSizeRef.current;
      if (Math.abs(width - prev.width) < 1 && Math.abs(height - prev.height) < 1) {
        return;
      }
      containerSizeRef.current = { width, height };

      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (atlasMode && mapViewCustomizedRef.current) {
          mapRef.current?.getMap()?.resize();
          return;
        }
        if (preserveMapPositionOnFilterChange) {
          fitMapToDefaultView(false);
          return;
        }
        fitMapToStops(false);
      });
    });

    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [bounds, mapReady, preserveMapPositionOnFilterChange, fitMapToDefaultView, fitMapToStops, mapRef]);

  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map?.isStyleLoaded?.()) return;
    applyMapboxBasemapTheme(map, mapTheme, map3dObjects, map3dBuildings, map3dTrees, showPoi);
  }, [mapTheme, map3dObjects, map3dBuildings, map3dTrees, showPoi, mapReady]);

  // Defer map rendering until we have coordinates to avoid showing a wrong location
  if (!boundsCenter) {
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          minHeight: compactMobile ? 240 : undefined,
        }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: compactMobile ? 240 : undefined,
        opacity: mapReady ? 1 : 0,
        pointerEvents: mapReady ? "auto" : "none",
      }}
    >
      <MapGL
        ref={instance => {
          mapRef.current = instance;
        }}
        mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
        mapStyle="mapbox://styles/tnb3k/cmmwgvqp5000701s6eq903b94"
        initialViewState={{
          longitude: boundsCenter.lng,
          latitude: boundsCenter.lat,
          zoom: 10,
          pitch: 0,
          bearing: 0,
        }}
        maxPitch={85}
        style={{ width: "100%", height: "100%" }}
        attributionControl={false}
        onLoad={e => {
          applyMapboxBasemapTheme(e.target, mapTheme, map3dObjects, map3dBuildings, map3dTrees, showPoi);
          setMapLoaded(true);
        }}
        onMoveStart={(e: ViewStateChangeEvent) => {
          if (e.originalEvent) {
            setIsAutoFollowActive(false);
            markMapViewCustomized();
          }
        }}
      >
        {!atlasMode &&
          !hasTrailOverlay &&
          mapReady &&
          data.itinerary?.routeStatus === "ready" &&
          data.itinerary.routePath && (
          <Source id="tripper-route-source" type="geojson" data={data.itinerary.routePath}>
            <Layer {...routeLayerStyle} />
          </Source>
        )}

        {!atlasMode && mapReady && hasTrailOverlay && trailOutline && (
          <Source
            id="tripper-trail-source"
            type="geojson"
            data={trailOutline as GeoJSON.GeoJSON}
          >
            <Layer {...trailLayerStyle} />
          </Source>
        )}

        {mapReady && placeOutline && (
          <Source
            id="tripper-place-source"
            type="geojson"
            data={placeOutline as GeoJSON.GeoJSON}
          >
            <Layer {...placeFillStyle} />
            <Layer {...placeLineStyle} />
          </Source>
        )}

        {mapReady && mapMarkers
          .slice()
          .sort((a, b) => {
            const aActive = activeMarkerIds.has(a.stop._id);
            const bActive = activeMarkerIds.has(b.stop._id);
            return (aActive ? 1 : 0) - (bActive ? 1 : 0);
          })
          .map(({ stop, coordinates, markerNumber }) => {
          const isActive = activeMarkerIds.has(stop._id);

          return (
            <Marker
              key={`${stop._id}-${mapReady}`}
              longitude={coordinates[0]}
              latitude={coordinates[1]}
              anchor="center"
              style={{ zIndex: isActive ? 10 : 1 }}
            >
              <button
                type="button"
                onClick={() => onMarkerClick(stop._id)}
                style={{
                  background: isMetadataInView || isActive
                    ? "var(--tripper-accent-bold)"
                    : "var(--tripper-text-subtlest)",
                  color: "#ffffff",
                  border: isActive && !isMetadataInView ? "2px solid var(--tripper-surface)" : "none",
                  borderRadius: "8px",
                  width: 32,
                  height: 32,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                  boxShadow: "0 0 4px 0 rgba(0, 0, 0, 0.40)",
                  transition: "all 200ms ease-out",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                {markerNumber}
              </button>
            </Marker>
          );
        })}
      </MapGL>

      {/* Status overlays */}
      {!atlasMode && data.itinerary?.routeStatus === "generating" && (
        <div
          style={{
            position: "absolute",
            top: 16,
            left: 16,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#ffffff",
            padding: "8px 14px",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#3B82F6",
              animation: "pulse-dot 1.4s ease-in-out infinite",
            }}
          />
          <style>{`@keyframes pulse-dot { 0%, 100% { opacity: .3; transform: scale(.85); } 50% { opacity: 1; transform: scale(1.1); } }`}</style>
          Generating route…
        </div>
      )}

      {!atlasMode && data.itinerary?.routeStatus === "failed" && (
        <div
          style={{
            position: "absolute",
            top: 16,
            left: 16,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#ffffff",
            padding: "8px 14px",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            fontSize: 13,
          }}
        >
          📍 Route unavailable — showing markers only
        </div>
      )}

      {/* Attribution */}
      <div
        style={{
          position: "absolute",
          bottom: 4,
          right: 8,
          fontSize: 10,
          color: "rgba(0,0,0,0.6)",
          background: "rgba(255,255,255,0.9)",
          padding: "2px 6px",
          borderRadius: 4,
        }}
      >
        ©{" "}
        <a
          href="https://www.mapbox.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit" }}
        >
          Mapbox
        </a>{" "}
        ©{" "}
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit" }}
        >
          OpenStreetMap
        </a>
      </div>

      {!hideAutoFollow && !atlasMode && (
        <TripperAutoFollowToggle
          enabled={isAutoFollowActive}
          onToggle={() => setIsAutoFollowActive(!isAutoFollowActive)}
        />
      )}

      {atlasMode && mapViewCustomized && (
        <TripperMapResetButton onClick={resetMapView} />
      )}

      {/* Bottom controls bar */}
      {!hideControls && (
        <div className="tripper-map-controls">
          {!atlasMode && (
            <button
              type="button"
              className="tripper-map-icon-btn"
              onClick={() => {
                fitMapToBounds(mapRef.current, boundsRef.current, { animated: true, padding: 64 });
              }}
              title="Re-center"
            >
              ⊙
            </button>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button
              type="button"
              className="tripper-map-icon-btn"
              onClick={() => {
                markMapViewCustomized();
                const currentZoom = mapRef.current?.getZoom() ?? 10;
                mapRef.current?.flyTo({ zoom: currentZoom - 1, duration: 250 });
              }}
            >
              −
            </button>
            <button
              type="button"
              className="tripper-map-icon-btn"
              onClick={() => {
                markMapViewCustomized();
                const currentZoom = mapRef.current?.getZoom() ?? 10;
                mapRef.current?.flyTo({ zoom: currentZoom + 1, duration: 250 });
              }}
            >
              +
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
